import { useState, useEffect, useCallback } from 'react';
import { CheckCircle2, XCircle, Send, KeyRound, UserPlus, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';

const statusColor = (s) => ({ 'Approved': 'bg-forest/10 text-forest', 'Approved With Conditions': 'bg-forest/10 text-forest', 'Submitted': 'bg-gold/15 text-gold-dark', 'Suspended': 'bg-red-50 text-red-600' }[s] || 'bg-dark/10 text-dark');

function genTempPassword() {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let out = '';
  for (let i = 0; i < 10; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

function CreateFarmPartnerDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', farm_name: '', temp_password: genTempPassword() });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/admin/farm-partners', { ...form, origin_url: window.location.origin });
      toast.success(res.data.message);
      setOpen(false);
      setForm({ name: '', email: '', phone: '', farm_name: '', temp_password: genTempPassword() });
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create farm partner');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn-primary py-2.5 px-5 text-sm inline-flex items-center gap-2" data-testid="open-create-farm-partner"><UserPlus className="w-4 h-4" />Create Farm Partner</button>
      </DialogTrigger>
      <DialogContent data-testid="create-farm-partner-dialog">
        <DialogHeader>
          <DialogTitle className="font-playfair">Create Farm Partner Account</DialogTitle>
          <DialogDescription>Creates a fully-approved farm partner account with a temporary password, bypassing the standard application review.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Contact name</Label><Input required className="bg-cream border-forest/20" value={form.name} onChange={(e) => set('name', e.target.value)} data-testid="create-fp-name" /></div>
          <div><Label>Farm / business name</Label><Input required className="bg-cream border-forest/20" value={form.farm_name} onChange={(e) => set('farm_name', e.target.value)} data-testid="create-fp-farm-name" /></div>
          <div><Label>Email</Label><Input required type="email" className="bg-cream border-forest/20" value={form.email} onChange={(e) => set('email', e.target.value)} data-testid="create-fp-email" /></div>
          <div><Label>Phone</Label><Input required className="bg-cream border-forest/20" value={form.phone} onChange={(e) => set('phone', e.target.value)} data-testid="create-fp-phone" /></div>
          <div>
            <Label>Temporary password</Label>
            <div className="flex gap-2">
              <Input required minLength={6} className="bg-cream border-forest/20" value={form.temp_password} onChange={(e) => set('temp_password', e.target.value)} data-testid="create-fp-temp-password" />
              <button type="button" onClick={() => set('temp_password', genTempPassword())} className="btn-secondary py-2 px-3 text-xs shrink-0">Regenerate</button>
            </div>
            <p className="font-manrope text-xs text-dark/50 mt-1">Expires in 72 hours if unused. Sent via welcome email — account starts fully Approved.</p>
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="submit-create-farm-partner">{loading ? 'Creating…' : 'Create Account & Send Welcome Email'}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminFarms() {
  const [farms, setFarms] = useState([]);
  const [sendingId, setSendingId] = useState(null);
  const load = useCallback(() => api.get('/admin/farms').then((r) => setFarms(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const profileDecision = async (farmId, decision) => {
    await api.post(`/admin/farms/${farmId}/profile-decision`, { decision, note: '' });
    toast.success(`Profile ${decision === 'Approve' ? 'approved & live' : 'returned'}`); load();
  };

  const sendResetLink = async (userId) => {
    setSendingId(userId);
    try {
      const res = await api.post(`/admin/users/${userId}/send-reset-link`, { origin_url: window.location.origin });
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send reset link');
    } finally {
      setSendingId(null);
    }
  };

  const resendTempPassword = async (userId) => {
    setSendingId(userId);
    try {
      const res = await api.post(`/admin/farm-partners/${userId}/resend-temp-password`, { temp_password: genTempPassword(), origin_url: window.location.origin });
      toast.success(res.data.message);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not resend temporary password');
    } finally {
      setSendingId(null);
    }
  };

  return (
    <div data-testid="admin-farms">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <p className="font-manrope text-dark/60">All farm partners. Approve public profile changes, manage onboarding, and reset access.</p>
        <CreateFarmPartnerDialog onCreated={load} />
      </div>
      <div className="bg-white rounded-2xl border border-forest/10 overflow-hidden">
        {farms.length === 0 ? <p className="p-12 text-center font-manrope text-dark/50">No farms yet.</p> : (
          <div className="divide-y divide-forest/10">
            {farms.map((f) => (
              <div key={f.farm_id} className="p-5 flex flex-wrap items-center gap-4" data-testid={`farm-${f.farm_id}`}>
                <div className="flex-1 min-w-[220px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-playfair text-lg font-bold text-dark">{f.name}</h3>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${statusColor(f.status)}`}>{f.status}</span>
                    {f.admin_created && <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-forest/5 text-forest/80 flex items-center gap-1" data-testid={`admin-created-badge-${f.farm_id}`}><Lock className="w-3 h-3" />Admin-created</span>}
                    {f.admin_created && f.temp_password_active && <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-gold/15 text-gold-dark">Temp password active</span>}
                    {f.admin_created && f.temp_password_expired && <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-50 text-red-600" data-testid={`temp-expired-badge-${f.farm_id}`}>Temp password expired</span>}
                  </div>
                  <p className="font-manrope text-sm text-dark/60">{f.application?.town || f.profile?.town || '—'}, NH · Profile: {f.profile_status} · Onboarding: {f.onboarding_completed}/{f.onboarding_total}</p>
                </div>
                <div className="flex gap-2 flex-wrap items-center">
                  {f.profile_status === 'Submitted' && (
                    <>
                      <button onClick={() => profileDecision(f.farm_id, 'Approve')} className="btn-primary py-2 px-4 text-sm" data-testid={`profile-approve-${f.farm_id}`}><CheckCircle2 className="w-4 h-4 mr-1" />Approve Profile</button>
                      <button onClick={() => profileDecision(f.farm_id, 'Decline')} className="btn-secondary py-2 px-4 text-sm" data-testid={`profile-decline-${f.farm_id}`}><XCircle className="w-4 h-4 mr-1" />Return</button>
                    </>
                  )}
                  {f.owner_id && (
                    f.admin_created && !f.password_setup_complete ? (
                      <button onClick={() => resendTempPassword(f.owner_id)} disabled={sendingId === f.owner_id} className="btn-secondary py-2 px-4 text-sm inline-flex items-center gap-1.5 disabled:opacity-50" data-testid={`resend-temp-password-${f.farm_id}`}>
                        <KeyRound className="w-3.5 h-3.5" />{sendingId === f.owner_id ? 'Sending…' : 'Reset & Resend Temporary Password'}
                      </button>
                    ) : (
                      <button onClick={() => sendResetLink(f.owner_id)} disabled={sendingId === f.owner_id} className="btn-secondary py-2 px-4 text-sm inline-flex items-center gap-1.5 disabled:opacity-50" data-testid={`send-reset-link-${f.farm_id}`}>
                        <Send className="w-3.5 h-3.5" />{sendingId === f.owner_id ? 'Sending…' : 'Send Reset Link'}
                      </button>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
